import React, { useState, useEffect } from 'react';
import { 
  getBlogs, 
  publishBlog, 
  archiveBlog, 
  deleteBlog, 
  searchBlogs,
  formatBlogDate,
  truncateText 
} from '../../api/blogApi';
import BlogGenerationForm from '../BlogGeneration/BlogGenerationForm';
import './BlogDashboard.scss';

const BlogDashboard = ({ clinicId }) => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [languageFilter, setLanguageFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showGenerationForm, setShowGenerationForm] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [showBlogModal, setShowBlogModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' },
    { value: 'archived', label: 'Archived' }
  ];

  const languageOptions = [
    { value: 'all', label: 'All Languages' },
    { value: 'en', label: 'English' },
    { value: 'hi', label: 'Hindi' },
    { value: 'gu', label: 'Gujarati' },
    { value: 'mr', label: 'Marathi' },
    { value: 'ta', label: 'Tamil' },
    { value: 'te', label: 'Telugu' },
    { value: 'kn', label: 'Kannada' },
    { value: 'ml', label: 'Malayalam' },
    { value: 'bn', label: 'Bengali' },
    { value: 'pa', label: 'Punjabi' }
  ];

  useEffect(() => {
    fetchBlogs();
  }, [clinicId, currentPage, statusFilter, languageFilter]);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        clinic_id: clinicId,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        language: languageFilter !== 'all' ? languageFilter : undefined,
      };

      const data = await getBlogs(params);
      setBlogs(data.results || data);
      setTotalPages(Math.ceil((data.count || data.length) / 10));
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchBlogs();
      return;
    }

    setLoading(true);
    try {
      const filters = {
        clinic_id: clinicId,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        page: currentPage,
      };

      const data = await searchBlogs(searchQuery, filters);
      setBlogs(data.results || data);
      setTotalPages(Math.ceil((data.count || data.length) / 10));
    } catch (error) {
      console.error('Error searching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action, blogId) => {
    setActionLoading(blogId);
    try {
      let result;
      switch (action) {
        case 'publish':
          result = await publishBlog(blogId);
          break;
        case 'archive':
          result = await archiveBlog(blogId);
          break;
        case 'delete':
          if (window.confirm('Are you sure you want to delete this blog post?')) {
            result = await deleteBlog(blogId);
          }
          break;
        default:
          return;
      }

      if (result) {
        // Refresh blogs list
        fetchBlogs();
      }
    } catch (error) {
      console.error(`Error ${action}ing blog:`, error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleBlogGenerated = (blogPost) => {
    setShowGenerationForm(false);
    fetchBlogs(); // Refresh the list
  };

  const openBlogModal = (blog) => {
    setSelectedBlog(blog);
    setShowBlogModal(true);
  };

  const closeBlogModal = () => {
    setShowBlogModal(false);
    setSelectedBlog(null);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { class: 'badge-secondary', icon: '📝' },
      published: { class: 'badge-success', icon: '✅' },
      archived: { class: 'badge-warning', icon: '📁' }
    };

    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span className={`badge ${config.class}`}>
        {config.icon} {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getLanguageBadge = (language) => {
    const languageNames = {
      en: 'English', hi: 'Hindi', gu: 'Gujarati', mr: 'Marathi',
      ta: 'Tamil', te: 'Telugu', kn: 'Kannada', ml: 'Malayalam',
      bn: 'Bengali', pa: 'Punjabi'
    };

    return (
      <span className="badge badge-info">
        🌐 {languageNames[language] || language}
      </span>
    );
  };

  if (showGenerationForm) {
    return (
      <div className="blog-dashboard">
        <div className="dashboard-header">
          <button 
            className="btn btn-outline-secondary"
            onClick={() => setShowGenerationForm(false)}
          >
            <i className="fas fa-arrow-left"></i> Back to Dashboard
          </button>
        </div>
        <BlogGenerationForm 
          onBlogGenerated={handleBlogGenerated}
          clinicId={clinicId}
        />
      </div>
    );
  }

  return (
    <div className="blog-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <h2>📚 Blog Management Dashboard</h2>
          <p>Manage your AI-generated blog content and SEO strategy</p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => setShowGenerationForm(true)}
        >
          <i className="fas fa-magic"></i> Generate New Blog
        </button>
      </div>

      <div className="dashboard-stats">
        <div className="stat-card">
          <div className="stat-icon">📝</div>
          <div className="stat-content">
            <h3>{blogs.filter(b => b.status === 'draft').length}</h3>
            <p>Draft Posts</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>{blogs.filter(b => b.status === 'published').length}</h3>
            <p>Published Posts</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🌐</div>
          <div className="stat-content">
            <h3>{new Set(blogs.map(b => b.language)).size}</h3>
            <p>Languages</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <h3>{blogs.reduce((acc, b) => acc + (b.target_keywords?.length || 0), 0)}</h3>
            <p>Target Keywords</p>
          </div>
        </div>
      </div>

      <div className="dashboard-controls">
        <div className="search-section">
          <div className="search-input">
            <input
              type="text"
              placeholder="Search blogs by title, content, or keywords..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button className="btn btn-outline-primary" onClick={handleSearch}>
              <i className="fas fa-search"></i>
            </button>
          </div>
        </div>

        <div className="filters-section">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="form-control"
          >
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            value={languageFilter}
            onChange={(e) => setLanguageFilter(e.target.value)}
            className="form-control"
          >
            {languageOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="blogs-table">
        {loading ? (
          <div className="loading-spinner">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Loading blogs...</p>
          </div>
        ) : blogs.length === 0 ? (
          <div className="no-blogs">
            <i className="fas fa-file-alt"></i>
            <h3>No blogs found</h3>
            <p>Start by generating your first AI-powered blog post!</p>
            <button 
              className="btn btn-primary"
              onClick={() => setShowGenerationForm(true)}
            >
              <i className="fas fa-magic"></i> Generate First Blog
            </button>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Status</th>
                    <th>Language</th>
                    <th>Keywords</th>
                    <th>Word Count</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {blogs.map(blog => (
                    <tr key={blog.id}>
                      <td>
                        <div className="blog-title">
                          <h6 onClick={() => openBlogModal(blog)}>
                            {blog.title}
                          </h6>
                          <p>{truncateText(blog.excerpt || blog.content, 80)}</p>
                        </div>
                      </td>
                      <td>{getStatusBadge(blog.status)}</td>
                      <td>{getLanguageBadge(blog.language)}</td>
                      <td>
                        <div className="keywords-display">
                          {blog.target_keywords?.slice(0, 3).map((keyword, index) => (
                            <span key={index} className="keyword-tag">
                              {keyword}
                            </span>
                          ))}
                          {blog.target_keywords?.length > 3 && (
                            <span className="keyword-more">
                              +{blog.target_keywords.length - 3}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="word-count">{blog.word_count}</span>
                      </td>
                      <td>
                        <span className="date">
                          {formatBlogDate(blog.created_at)}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          {blog.status === 'draft' && (
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleAction('publish', blog.id)}
                              disabled={actionLoading === blog.id}
                            >
                              {actionLoading === blog.id ? (
                                <i className="fas fa-spinner fa-spin"></i>
                              ) : (
                                <i className="fas fa-check"></i>
                              )}
                            </button>
                          )}
                          
                          {blog.status === 'published' && (
                            <button
                              className="btn btn-sm btn-warning"
                              onClick={() => handleAction('archive', blog.id)}
                              disabled={actionLoading === blog.id}
                            >
                              {actionLoading === blog.id ? (
                                <i className="fas fa-spinner fa-spin"></i>
                              ) : (
                                <i className="fas fa-archive"></i>
                              )}
                            </button>
                          )}

                          <button
                            className="btn btn-sm btn-info"
                            onClick={() => openBlogModal(blog)}
                          >
                            <i className="fas fa-eye"></i>
                          </button>

                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleAction('delete', blog.id)}
                            disabled={actionLoading === blog.id}
                          >
                            {actionLoading === blog.id ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              <i className="fas fa-trash"></i>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="btn btn-outline-primary"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => prev - 1)}
                >
                  <i className="fas fa-chevron-left"></i> Previous
                </button>
                
                <span className="page-info">
                  Page {currentPage} of {totalPages}
                </span>
                
                <button
                  className="btn btn-outline-primary"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                >
                  Next <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Blog Preview Modal */}
      {showBlogModal && selectedBlog && (
        <div className="modal-overlay" onClick={closeBlogModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedBlog.title}</h3>
              <button className="close-btn" onClick={closeBlogModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="modal-body">
              <div className="blog-meta">
                <div className="meta-item">
                  <strong>Status:</strong> {getStatusBadge(selectedBlog.status)}
                </div>
                <div className="meta-item">
                  <strong>Language:</strong> {getLanguageBadge(selectedBlog.language)}
                </div>
                <div className="meta-item">
                  <strong>Word Count:</strong> {selectedBlog.word_count}
                </div>
                <div className="meta-item">
                  <strong>Created:</strong> {formatBlogDate(selectedBlog.created_at)}
                </div>
              </div>

              <div className="blog-content">
                <h4>Content Preview</h4>
                <div 
                  className="content-preview"
                  dangerouslySetInnerHTML={{ __html: selectedBlog.content }}
                />
              </div>

              {selectedBlog.faqs && selectedBlog.faqs.length > 0 && (
                <div className="blog-faqs">
                  <h4>Generated FAQs</h4>
                  {selectedBlog.faqs.map((faq, index) => (
                    <div key={index} className="faq-item">
                      <h6>Q: {faq.question}</h6>
                      <p>A: {faq.answer}</p>
                    </div>
                  ))}
                </div>
              )}

              {selectedBlog.ctas && selectedBlog.ctas.length > 0 && (
                <div className="blog-ctas">
                  <h4>Call-to-Actions</h4>
                  {selectedBlog.ctas.map((cta, index) => (
                    <div key={index} className="cta-item">
                      <strong>{cta.cta_type}:</strong> {cta.text}
                      <div className="cta-button">
                        {cta.button_text}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogDashboard;
